import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Configure retry options
const maxRetries = 3;
const retryDelay = 1000; // 1 second

// Custom fetch with retry logic
const fetchWithRetry = async (url: string, options: RequestInit, attempt = 1): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error) {
    if (attempt >= maxRetries) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
    return fetchWithRetry(url, options, attempt + 1);
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'abcbrain-xyz'
    },
    fetch: fetchWithRetry
  }
});

// Helper to check connection with retry
export const checkSupabaseConnection = async () => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const { error } = await supabase.from('sessions').select('id').limit(1);
      if (!error) return true;
      
      // Wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    } catch (error) {
      console.error(`Connection check attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) return false;
      await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
    }
  }
  return false;
};

// Helper to handle realtime subscription errors
export const handleRealtimeError = (error: Error) => {
  console.error('Realtime subscription error:', error);
  // You could implement additional error handling here
  // For example, showing a notification to the user
};

// Helper to reconnect realtime subscription
export const reconnectRealtime = async () => {
  try {
    await supabase.removeAllChannels();
    const { error } = await supabase.channel('system').subscribe();
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to reconnect realtime:', error);
    return false;
  }
};